import { ArrowLeft, User, Bike, FileText, CreditCard, Bell, HelpCircle, LogOut, ChevronRight, Shield, Phone, Mail, PersonStanding, Loader2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import { auth } from '../../config/firebase.config';
import { signOut } from 'firebase/auth';
import { dbService } from '../../services/firebase-db.service';

interface ProfileScreenProps {
  onBack: () => void;
  onNavigateToSettings?: () => void;
  onNavigateToFleetTypeSelection?: () => void;
  onNavigateToEditProfile?: () => void;
  onNavigateToGearCompliance?: () => void;
  riderFleetType?: 'rider' | 'bicycle' | 'leg-fleet';
}

export function ProfileScreen({ onBack, onNavigateToSettings, onNavigateToFleetTypeSelection, onNavigateToEditProfile, onNavigateToGearCompliance, riderFleetType = 'rider' }: ProfileScreenProps) {
  const [rider, setRider] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const user = auth.currentUser;
      if (user) {
        const profile = await dbService.getRiderProfile(user.uid);
        setRider({
          name: profile?.name || 'Rider',
          phone: profile?.phone || user.email?.replace('@go7.rider', '') || 'N/A',
          email: profile?.email || user.email || 'N/A',
          rating: '4.9',
          totalDeliveries: 0,
          memberSince: new Date(user.metadata.creationTime || Date.now()).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
          vehicle: 'Not Set',
          status: 'verified' as const,
          fleetType: profile?.fleetType || riderFleetType
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
      setRider({
        name: 'Rider',
        phone: 'N/A',
        email: 'N/A',
        rating: '4.9',
        totalDeliveries: 0,
        memberSince: 'Recently',
        vehicle: 'Not Set',
        status: 'verified' as const,
        fleetType: riderFleetType
      });
    } finally {
      setLoading(false);
    }
  };

  const getFleetTypeInfo = (fleetType: string) => {
    const configs = {
      'rider': { label: 'Rider (Motorcycle)', icon: Bike, bgColor: 'bg-[#E0001B]/10', textColor: 'text-[#E0001B]', description: 'Motorcycle deliveries (8km+)' },
      'bicycle': { label: 'Bicycle', icon: Bike, bgColor: 'bg-[#0066CC]/10', textColor: 'text-[#0066CC]', description: 'Bicycle deliveries (2-8km)' },
      'leg-fleet': { label: 'Leg Fleet (Walking)', icon: PersonStanding, bgColor: 'bg-[#00B277]/10', textColor: 'text-[#00B277]', description: 'Walking deliveries (<2km)' }
    };
    return configs[fleetType as keyof typeof configs] || configs.rider;
  };

  const fleetInfo = rider ? getFleetTypeInfo(rider.fleetType) : getFleetTypeInfo('rider');
  const FleetIcon = fleetInfo.icon;

  const onLogout = async () => {
    if (confirm('Are you sure you want to logout?')) {
      try {
        await signOut(auth);
        console.log('✅ Logged out successfully');
        // Reload the page to trigger auth state change and go to login
        window.location.reload();
      } catch (error) {
        console.error('❌ Logout failed:', error);
        alert('Failed to logout. Please try again.');
      }
    }
  };

  const menuItems: Array<{
    icon: any;
    label: string;
    description: string;
    action: () => void;
    badge?: string;
    badgeColor?: string;
  }> = [
    {
      icon: User,
      label: 'Edit Profile',
      description: 'Update your personal information',
      action: () => {
        if (onNavigateToEditProfile) {
          onNavigateToEditProfile();
        }
      },
    },
    {
      icon: FleetIcon,
      label: 'Fleet Type',
      description: fleetInfo.label,
      action: () => {
        if (onNavigateToFleetTypeSelection) {
          onNavigateToFleetTypeSelection();
        }
      },
    },
    {
      icon: Shield,
      label: 'Gear Compliance',
      description: 'Check your gear compliance status',
      action: () => {
        if (onNavigateToGearCompliance) {
          onNavigateToGearCompliance();
        }
      },
    },
  ];

  if (loading) {
    return (
      <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#E0001B] animate-spin" />
      </div>
    );
  }

  if (!rider) {
    return (
      <div className="absolute inset-0 bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-500">Failed to load profile</p>
          <button onClick={loadProfile} className="mt-4 text-[#E0001B]">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="absolute inset-0 bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-2 hover:bg-gray-50 rounded-lg transition-colors">
          <ArrowLeft className="w-6 h-6 text-[#111111]" />
        </button>
        <h1 className="text-[18px] text-[#111111]">Profile & Settings</h1>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain">
        {/* Profile Header */}
        <div className="bg-white p-6 border-b border-gray-100">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-20 h-20 rounded-full bg-[#E0001B]/10 flex items-center justify-center">
              <span className="text-[28px] text-[#E0001B]">
                {rider.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
              </span>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-[18px] text-[#111111]">{rider.name}</h2>
                {rider.status === 'verified' && (
                  <div className="w-5 h-5 rounded-full bg-[#00B277] flex items-center justify-center">
                    <span className="text-white text-[12px]">✓</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-3">
                <span className="px-2 py-1 bg-[#00B277]/10 text-[#00B277] rounded text-[12px]">
                  Pro Rider
                </span>
                <span className="text-[14px] text-[#6B6B6B]">{rider.rating} ★</span>
              </div>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-[18px] text-[#111111]">{rider.totalDeliveries}</p>
              <p className="text-[12px] text-[#6B6B6B]">Deliveries</p>
            </div>
            <div className="text-center">
              <p className="text-[18px] text-[#111111]">{rider.rating}</p>
              <p className="text-[12px] text-[#6B6B6B]">Rating</p>
            </div>
            <div className="text-center">
              <p className="text-[18px] text-[#111111]">{rider.vehicle}</p>
              <p className="text-[12px] text-[#6B6B6B]">Vehicle</p>
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white mt-2 p-4 border-b border-gray-100">
          <p className="text-[12px] text-[#6B6B6B] mb-3">CONTACT INFORMATION</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-[#6B6B6B]" />
              <span className="text-[14px] text-[#111111]">{rider.phone}</span>
            </div>
            {rider.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#6B6B6B]" />
                <span className="text-[14px] text-[#111111]">{rider.email}</span>
              </div>
            )}
          </div>
        </div>

        {/* Menu Items */}
        <div className="bg-white mt-2 divide-y divide-gray-100">
          {menuItems.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={item.action}
                className="w-full px-4 py-4 flex items-center gap-3 hover:bg-gray-50 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-[#111111]" />
                </div>
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-[14px] text-[#111111]">{item.label}</p>
                    {item.badge && (
                      <span className={`px-2 py-0.5 rounded text-[10px] ${item.badgeColor}`}>
                        {item.badge}
                      </span>
                    )}
                  </div>
                  <p className="text-[12px] text-[#6B6B6B]">{item.description}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-[#6B6B6B]" />
              </button>
            );
          })}
        </div>

        {/* App Info */}
        <div className="p-4 mt-2">
          <div className="bg-white rounded-[12px] p-4 border border-gray-100 text-center">
            <p className="text-[12px] text-[#6B6B6B] mb-1">GO7 Rider App</p>
            <p className="text-[14px] text-[#111111] mb-2">Version 2.4.1</p>
            <p className="text-[12px] text-[#6B6B6B]">Member since {rider.memberSince}</p>
          </div>
        </div>

        {/* Logout Button */}
        <div className="p-4">
          <button
            onClick={onLogout}
            className="w-full h-12 rounded-[12px] border-2 border-[#E02020] text-[#E02020] hover:bg-[#E02020]/5 transition-colors flex items-center justify-center gap-2"
          >
            <LogOut className="w-5 h-5" />
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}